import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Search, MoreVertical, Gift, Ban, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  trialing: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  active:   "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  canceled: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
  expired:  "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
  past_due: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
};

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "2-digit" }); }
  catch { return s; }
}

export default function AdminUsers() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [grantUser, setGrantUser] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", q, status],
    queryFn: async () => (await api.get("/admin/users", {
      params: { q: q || undefined, status: status === "all" ? undefined : status, limit: 200 },
    })).data,
  });

  const suspend = useMutation({
    mutationFn: async ({ user_id, is_suspended }) =>
      (await api.post(`/admin/users/${user_id}/suspend`, { is_suspended })).data,
    onSuccess: (_, vars) => {
      toast.success(vars.is_suspended ? "Usuario suspendido" : "Usuario reactivado");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al actualizar"),
  });

  const cancelSub = useMutation({
    mutationFn: async (user_id) => (await api.post(`/admin/users/${user_id}/cancel`)).data,
    onSuccess: () => {
      toast.success("Suscripción cancelada");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al cancelar"),
  });

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">Usuarios</h1>
        <p className="text-sm text-muted-foreground mt-1">{data?.total ?? 0} usuarios registrados.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="admin-users-search"
            placeholder="Buscar por nombre o email…"
            className="pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-users-status-filter" className="md:w-48">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="trialing">En prueba</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="past_due">Vencidos</SelectItem>
            <SelectItem value="canceled">Cancelados</SelectItem>
            <SelectItem value="expired">Expirados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Sin usuarios para esos filtros.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-white/40 dark:border-white/10">
                  <tr>
                    <th className="px-4 py-3">Usuario</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 hidden md:table-cell">Plan</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Vence</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Creado</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u) => (
                    <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}
                        className="border-b border-white/30 dark:border-white/5 last:border-0 hover:bg-white/30 dark:hover:bg-white/5 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-full bg-blue-500/15 grid place-items-center text-xs font-semibold text-blue-600 dark:text-blue-300 flex-none">
                            {(u.name || u.email || "?")[0]?.toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium truncate flex items-center gap-1.5">
                              {u.name || "—"}
                              {u.is_admin && <ShieldCheck className="h-3.5 w-3.5 text-red-500" aria-label="Admin" />}
                              {u.is_suspended && <Ban className="h-3.5 w-3.5 text-rose-500" aria-label="Suspendido" />}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={STATUS_COLORS[u.subscription_status] || ""}>
                          {u.subscription_status || "—"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {u.plan
                          ? <span className="capitalize">{u.plan} · {u.billing_cycle} · {(u.currency || "").toUpperCase()}{u.seats > 1 ? ` · ${u.seats}u` : ""}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">
                        {fmtDate(u.current_period_end || u.trial_ends_at)}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button data-testid={`admin-user-actions-${u.user_id}`} variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem data-testid={`admin-user-grant-${u.user_id}`} onClick={() => setGrantUser(u)}>
                              <Gift className="h-4 w-4 mr-2" /> Otorgar suscripción
                            </DropdownMenuItem>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem data-testid={`admin-user-cancel-${u.user_id}`} onSelect={(e) => e.preventDefault()}>
                                  <XCircle className="h-4 w-4 mr-2" /> Cancelar suscripción
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Cancelar suscripción</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Se marcará la suscripción de <b>{u.email}</b> como <i>canceled</i>.
                                    Esto no genera un reembolso automático en PayPal.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Volver</AlertDialogCancel>
                                  <AlertDialogAction
                                    data-testid={`admin-user-cancel-confirm-${u.user_id}`}
                                    onClick={() => cancelSub.mutate(u.user_id)}
                                  >
                                    Sí, cancelar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            <DropdownMenuSeparator />
                            {u.is_suspended ? (
                              <DropdownMenuItem
                                data-testid={`admin-user-unsuspend-${u.user_id}`}
                                onClick={() => suspend.mutate({ user_id: u.user_id, is_suspended: false })}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-2" /> Reactivar acceso
                              </DropdownMenuItem>
                            ) : (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem
                                    data-testid={`admin-user-suspend-${u.user_id}`}
                                    onSelect={(e) => e.preventDefault()}
                                    className="text-rose-600 dark:text-rose-400"
                                  >
                                    <Ban className="h-4 w-4 mr-2" /> Suspender usuario
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Suspender a {u.email}</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Se invalidan sus sesiones y no podrá ingresar hasta reactivarlo.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Volver</AlertDialogCancel>
                                    <AlertDialogAction
                                      data-testid={`admin-user-suspend-confirm-${u.user_id}`}
                                      onClick={() => suspend.mutate({ user_id: u.user_id, is_suspended: true })}
                                    >
                                      Sí, suspender
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <GrantDialog user={grantUser} onClose={() => setGrantUser(null)} />
    </div>
  );
}

function GrantDialog({ user, onClose }) {
  const qc = useQueryClient();
  const [plan, setPlan] = useState("pro");
  const [cycle, setCycle] = useState("monthly");
  const [currency, setCurrency] = useState("usd");
  const [seats, setSeats] = useState(1);
  const [days, setDays] = useState(30);

  const grant = useMutation({
    mutationFn: async () =>
      (await api.post(`/admin/users/${user.user_id}/grant`, {
        plan, cycle, currency, seats: Number(seats) || 1, days: Number(days) || 30,
      })).data,
    onSuccess: () => {
      toast.success("Suscripción otorgada");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      qc.invalidateQueries({ queryKey: ["admin-payments"] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al otorgar"),
  });

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="admin-grant-dialog">
        <DialogHeader>
          <DialogTitle>Otorgar suscripción</DialogTitle>
          <DialogDescription>
            Asigna una suscripción gratuita (comp) a <b>{user?.email}</b>. No se cobra nada.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Plan</label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger data-testid="grant-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="corporate">Corporativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Ciclo</label>
            <Select value={cycle} onValueChange={setCycle}>
              <SelectTrigger data-testid="grant-cycle"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Mensual</SelectItem>
                <SelectItem value="annual">Anual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Moneda</label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="grant-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="usd">USD</SelectItem>
                <SelectItem value="mxn">MXN</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Asientos</label>
            <Input data-testid="grant-seats" type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Duración (días)</label>
            <Input data-testid="grant-days" type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button data-testid="grant-submit" onClick={() => grant.mutate()} disabled={grant.isPending}>
            {grant.isPending ? "Otorgando…" : "Otorgar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
